<script setup lang="ts">
import { useAlarmStore } from '@/store/alarm'
import type { IAlarmListingDynamicFilter } from '@/store/alarm'
import { useErrorStore } from '@/store/error'
import { useLoadingStore } from '@/store/loading'
import { formatDateWithTime } from '@/utils/date/date-time'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

type TableColumn = {
  title: string
  key: string
  sortable?: boolean
  onlyGroup?: boolean
  requiresCompanyData?: boolean
}

const { t } = useI18n()
const router = useRouter()

const props = defineProps({
  classifiedFilter: {
    type: [Boolean, null],
    default: null,
  },
  scopeFilter: {
    type: String,
    default: 'individual',
  },
})

const alarmStore = useAlarmStore()
const isLoading = useLoadingStore()
const error = useErrorStore()
const listingState = alarmStore.getListingTableState || ({} as any)
const itemsPerPageOptions = ref([10, 25, 50, 100])
const showColumnSelector = ref(false)
const page = ref(listingState.page || 1)
const items = ref(listingState.items || 10)
const sort = ref(listingState.sort || 'id')
const sortDirection = ref<'asc' | 'desc'>(listingState.sortDirection || 'desc')
const columnWidthsState = ref<Record<string, number>>({ ...(listingState.columnWidths || {}) })
const hasMounted = ref(false)
const isFetchingPage = ref(false)

const hasCompanies = computed(() => (alarmStore.listAlarms ?? []).some((a) => (a?.sensor?.companies?.length ?? 0) > 0))
const totalPages = computed(() => alarmStore.getPagination?.pages || 1)

const paginationButtonClass =
  'min-w-[2.5rem] rounded-none border border-stroke px-3 py-2 text-sm font-medium transition-colors'
const visibleColumnsState = ref<string[]>(
  Array.isArray(listingState.visibleColumns)
    ? [...listingState.visibleColumns]
    : ['id', 'name', 'company', 'sensor', 'group', 'start_date', 'end_date', 'shift', 'classification_type'],
)

const allColumns = computed<TableColumn[]>(() => [
  { title: t('alarm_listing.body.table.columns.id'), key: 'id', sortable: true },
  { title: t('alarm_listing.body.table.columns.name'), key: 'name', sortable: true },
  { title: t('alarm_listing.body.table.columns.company'), key: 'company', requiresCompanyData: true },
  { title: t('alarm_listing.body.table.columns.sensor'), key: 'sensor', sortable: true },
  { title: t('alarm_listing.body.table.columns.group'), key: 'group', sortable: true, onlyGroup: true },
  { title: t('alarm_listing.body.table.columns.start_date'), key: 'start_date', sortable: true },
  { title: t('alarm_listing.body.table.columns.end_date'), key: 'end_date', sortable: true },
  { title: t('alarm_listing.body.table.columns.shift'), key: 'shift' },
  { title: t('alarm_listing.body.table.columns.classification_type'), key: 'classification_type', sortable: true },
])

const columns = computed(() =>
  allColumns.value.filter((column) => {
    const allowedByScope = !column.onlyGroup || props.scopeFilter === 'group'
    const allowedByCompanyData = !column.requiresCompanyData || hasCompanies.value
    const visible = visibleColumnsState.value.includes(column.key)
    return allowedByScope && allowedByCompanyData && visible
  }),
)

watch(
  [() => props.classifiedFilter, () => props.scopeFilter, () => alarmStore.getListingTableState?.dynamicFilters],
  () => {
    if (!hasMounted.value) return
    loadPage(1)
  },
  { deep: true },
)

watch([page, items, sort, sortDirection, visibleColumnsState, columnWidthsState], () => {
  alarmStore.setListingTableState({
    page: page.value,
    items: items.value,
    sort: sort.value,
    sortDirection: sortDirection.value,
    visibleColumns: [...visibleColumnsState.value],
    columnWidths: { ...columnWidthsState.value },
  })
})

onMounted(() => {
  hasMounted.value = true
  loadPage(page.value)
})

function handleSort(field: string) {
  if (sort.value === field) {
    sortDirection.value = sortDirection.value === 'desc' ? 'asc' : 'desc'
  } else {
    sort.value = field
    sortDirection.value = 'desc'
  }
  loadPage(1)
}

function normalizedClassifiedFilter(): boolean | null {
  return typeof props.classifiedFilter === 'boolean' ? props.classifiedFilter : null
}

async function loadPage(pageNumber: number) {
  if (isFetchingPage.value) return
  page.value = pageNumber
  const currentFilters = (alarmStore.getListingTableState.dynamicFilters || []) as IAlarmListingDynamicFilter[]
  isFetchingPage.value = true
  try {
    await alarmStore.getAlarmsPages(page.value, items.value, sort.value, sortDirection.value, normalizedClassifiedFilter(), currentFilters)
  } finally {
    isFetchingPage.value = false
  }
}

function handleItemsPerPageChange(event: Event) {
  const target = event.target as HTMLSelectElement
  items.value = Number.parseInt(target.value, 10)
  loadPage(1)
}

function formatShiftArray(dataItem: any) {
  return Array.isArray(dataItem.shifts) && dataItem.shifts.length > 0 ? dataItem.shifts.map((s) => s.name).join(', ') : '-'
}

function getVisiblePages(currentPage: number, totalPages: number, maxVisible: number = 7) {
  const pages: (number | string)[] = []
  if (totalPages <= maxVisible) {
    for (let i = 1; i <= totalPages; i++) pages.push(i)
    return pages
  }

  pages.push(1)
  if (currentPage <= 4) {
    for (let i = 2; i <= 5; i++) pages.push(i)
    pages.push('...')
    pages.push(totalPages)
  } else if (currentPage >= totalPages - 3) {
    pages.push('...')
    for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i)
  } else {
    pages.push('...')
    for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i)
    pages.push('...')
    pages.push(totalPages)
  }

  return pages
}

function toggleColumn(key: string) {
  if (visibleColumnsState.value.includes(key)) {
    visibleColumnsState.value = visibleColumnsState.value.filter((columnKey) => columnKey !== key)
    return
  }
  visibleColumnsState.value = [...visibleColumnsState.value, key]
}

function getColumnWidth(columnKey: string): number {
  return columnWidthsState.value[columnKey] || 160
}

function setColumnWidth(columnKey: string, widthValue: string) {
  const parsed = Number.parseInt(widthValue, 10)
  if (Number.isNaN(parsed)) return
  const normalized = Math.max(90, Math.min(600, parsed))
  columnWidthsState.value = {
    ...columnWidthsState.value,
    [columnKey]: normalized,
  }
}

function openAlarmDetails(dataItem: any) {
  const groupId = dataItem?.group?.id
  const sensorId = dataItem?.sensor?.id || 0
  if (!sensorId && groupId) {
    router.push({
      name: 'group-alarm-detail',
      params: { groupId: String(groupId), alarmId: String(dataItem.id) },
      query: { alarmType: dataItem.alarm_type ?? '' },
    })
    return
  }
  router.push({
    name: 'alarm-detail',
    params: { sensorId: String(sensorId), alarmId: String(dataItem.id) },
  })
}

function columnValue(dataItem: any, key: string): string {
  switch (key) {
    case 'id':
      return String(dataItem.id ?? '-')
    case 'name':
      return dataItem.name ?? '-'
    case 'company':
      return dataItem.sensor?.companies?.[0]?.name ?? '-'
    case 'sensor':
      return dataItem.sensor?.name ?? '-'
    case 'group':
      return dataItem.group?.name ?? '-'
    case 'start_date':
      return formatDateWithTime(dataItem.start_date) ?? '-'
    case 'end_date':
      return formatDateWithTime(dataItem.end_date) ?? '-'
    case 'shift':
      return formatShiftArray(dataItem)
    case 'classification_type':
      return dataItem.classification_type?.name ?? '-'
    default:
      return '-'
  }
}
</script>

<template>
  <div class="bg-white py-2 shadow dark:bg-gray-900">
    <div v-if="isLoading.loading" class="flex items-center justify-center py-6">
      <svg class="h-5 w-5 animate-spin text-blue-600 lg:h-7 lg:w-7 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    </div>

    <div v-else-if="error.hasErrors" class="p-4 text-red-500">
      {{ t('alarm_listing.errors.error_data') }}
    </div>

    <div v-else-if="(alarmStore.listAlarms ?? []).length <= 0" class="p-4 text-red-500">
      {{ t('alarm_listing.errors.no_data_found') }}
    </div>

    <div v-else>
      <div class="mb-4 flex flex-wrap items-center justify-between gap-3 px-2">
        <div class="text-sm text-gray-500">
          {{ t('alarm_listing.body.table.total_records', { count: alarmStore.getPagination?.count ?? 0 }) }}
        </div>

        <div class="relative">
          <button type="button" class="rounded-none border border-stroke px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-2" @click="showColumnSelector = !showColumnSelector">
            {{ t('alarm_listing.body.table.columns_button') }}
          </button>
          <div v-if="showColumnSelector" class="absolute right-0 z-20 mt-2 w-56 rounded-none border border-stroke bg-white p-3 shadow-lg">
            <label
              v-for="column in allColumns.filter((c) => (!c.onlyGroup || props.scopeFilter === 'group') && (!c.requiresCompanyData || hasCompanies))"
              :key="column.key"
              class="mb-2 flex cursor-pointer items-center gap-2 text-sm text-slate-700 last:mb-0">
              <input type="checkbox" :checked="visibleColumnsState.includes(column.key)" @change="toggleColumn(column.key)" />
              {{ column.title }}
            </label>
            <div class="mt-3 border-t border-slate-200 pt-2">
              <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {{ t('alarm_listing.body.table.column_width_px') }}
              </p>
              <div
                v-for="column in allColumns.filter((c) => (!c.onlyGroup || props.scopeFilter === 'group') && (!c.requiresCompanyData || hasCompanies))"
                :key="`width-${column.key}`"
                class="mb-2 last:mb-0">
                <label class="mb-1 block text-xs text-slate-600">{{ column.title }}</label>
                <input
                  type="number"
                  min="90"
                  max="600"
                  :value="getColumnWidth(column.key)"
                  class="w-full rounded-none border border-stroke px-2 py-1 text-xs"
                  @input="setColumnWidth(column.key, ($event.target as HTMLInputElement).value)" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full table-auto text-left text-sm text-gray-700 dark:text-gray-200">
          <thead>
            <tr class="bg-gray-100 text-xs font-semibold uppercase text-gray-700 dark:bg-gray-800 dark:text-gray-200">
              <th
                v-for="column in columns"
                :key="column.key"
                scope="col"
                class="whitespace-nowrap px-4 py-3"
                :style="{ width: `${getColumnWidth(column.key)}px`, minWidth: `${getColumnWidth(column.key)}px` }"
                :class="{ 'cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700': column.sortable }"
                @click="column.sortable ? handleSort(column.key) : null">
                <div class="flex items-center justify-between gap-2">
                  <span>{{ column.title }}</span>
                  <span v-if="column.sortable" class="text-blue-600">
                    <span v-if="sort === column.key && sortDirection === 'asc'">▲</span>
                    <span v-else-if="sort === column.key && sortDirection === 'desc'">▼</span>
                  </span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(dataItem, index) in (alarmStore.listAlarms ?? [])"
              :key="dataItem.id ?? index"
              class="cursor-pointer border-b border-gray-200 hover:bg-slate-100 dark:border-gray-700 dark:hover:bg-slate-800"
              :class="index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-850'"
              @click="openAlarmDetails(dataItem)">
              <td
                v-for="column in columns"
                :key="`${dataItem.id}-${column.key}`"
                class="whitespace-nowrap px-4 py-3"
                :style="{ width: `${getColumnWidth(column.key)}px`, minWidth: `${getColumnWidth(column.key)}px` }">
                {{ columnValue(dataItem, column.key) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="mt-4 flex flex-col items-center justify-between gap-4 px-2 sm:flex-row">
        <div class="flex items-center gap-2 text-sm text-gray-500">
          <span>{{ t('alarm_listing.body.table.items_per_page') }}</span>
          <select id="itemsPerPage" v-model="items" @change="handleItemsPerPageChange" class="rounded-none border border-stroke bg-white px-2 py-1 text-sm text-gray-700">
            <option v-for="option in itemsPerPageOptions" :key="option" :value="option">
              {{ option.toString().padStart(2, '0') }}
            </option>
          </select>
        </div>

        <div class="text-sm text-gray-500">
          {{
            t('alarm_listing.body.table.showing_range', {
              start: (page - 1) * items + 1,
              end: Math.min(page * items, alarmStore.getPagination?.count || 0),
              total: alarmStore.getPagination?.count || 0,
            })
          }}
        </div>

        <div v-if="alarmStore.getPagination" class="flex items-center gap-1">
          <button
            type="button"
            :disabled="!alarmStore.getPagination.prev || isFetchingPage"
            :class="[
              paginationButtonClass,
              alarmStore.getPagination.prev && !isFetchingPage
                ? 'bg-white text-gray-700 hover:bg-gray-2'
                : 'cursor-not-allowed bg-gray-2 text-gray-400 opacity-50',
            ]"
            @click="loadPage(alarmStore.getPagination.prev || 1)">
            &lt;
          </button>

          <template v-for="n in getVisiblePages(page, totalPages)" :key="n">
            <button
              v-if="typeof n === 'number'"
              type="button"
              :disabled="isFetchingPage"
              :class="[
                paginationButtonClass,
                n === page
                  ? 'border-primary bg-primary text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-2 disabled:opacity-50',
              ]"
              @click="loadPage(n)">
              {{ n }}
            </button>
            <span v-else class="px-2 py-2 text-sm text-gray-500">{{ n }}</span>
          </template>

          <button
            type="button"
            :disabled="!alarmStore.getPagination.next || isFetchingPage"
            :class="[
              paginationButtonClass,
              alarmStore.getPagination.next && !isFetchingPage
                ? 'bg-white text-gray-700 hover:bg-gray-2'
                : 'cursor-not-allowed bg-gray-2 text-gray-400 opacity-50',
            ]"
            @click="loadPage(alarmStore.getPagination.next || alarmStore.getPagination.pages)">
            &gt;
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

